import { useCallback, useEffect, useRef, useState } from "react";
import { fetchTimerState, saveTimerState } from "../../lib/api/timer-state.api";
import { hasUserId } from "../../lib/api/client";
import { track } from "../../lib/analytics";
import { getNotificationSettings } from "../../features/settings/hooks/use-notification-settings";
import { sendNotification } from "./use-notifications";

export interface TimerState<M extends string> {
  readonly isRunning: boolean;
  readonly timeLeft: number;
  readonly mode: M;
  readonly counter: number;
}

export interface TimerHookReturn<M extends string> {
  readonly state: TimerState<M>;
  readonly start: () => void;
  readonly pause: () => void;
  readonly reset: () => void;
  readonly skip: () => void;
}

interface PersistedTimer<M extends string> {
  mode: M;
  timeLeft: number;
  counter: number;
  updatedAt?: number;
}

interface TimerConfig<M extends string> {
  readonly storageKey: string;
  readonly serverKey: string;
  readonly validModes: readonly M[];
  readonly defaultMode: M;
  /** Ancien nom du champ counter en localStorage/serveur — ecrit en double pour retro-compatibilite */
  readonly legacyCounterKey: string;
  readonly getDefaultDuration: () => number;
  readonly getTransition: (prev: TimerState<M>) => { mode: M; timeLeft: number; counter: number };
  readonly getAutoStart: (fromSkip: boolean) => boolean;
  readonly onTransitionSound: (endedMode: M) => void;
  readonly getNotificationMessage?: (endedMode: M) => string | null;
}

function extractCounter(o: Record<string, unknown>): number | null {
  if (typeof o.counter === "number") return o.counter;
  // Migration: ancien format utilisait sessionsCompleted / cyclesCompleted
  if (typeof o.sessionsCompleted === "number") return o.sessionsCompleted;
  if (typeof o.cyclesCompleted === "number") return o.cyclesCompleted;
  return null;
}

function isValidPersisted<M extends string>(v: unknown, validModes: readonly M[]): v is PersistedTimer<M> {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    (validModes as readonly string[]).includes(o.mode as string) &&
    typeof o.timeLeft === "number" &&
    extractCounter(o) !== null
  );
}

export function createTimerHook<M extends string>(config: TimerConfig<M>) {
  const { storageKey, serverKey, validModes, defaultMode } = config;

  function loadLocal(): Pick<TimerState<M>, "mode" | "timeLeft" | "counter"> {
    const defaultDuration = config.getDefaultDuration();
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return { mode: defaultMode, timeLeft: defaultDuration, counter: 0 };
      const parsed: unknown = JSON.parse(raw);
      if (!isValidPersisted(parsed, validModes)) return { mode: defaultMode, timeLeft: defaultDuration, counter: 0 };
      const o = parsed as unknown as Record<string, unknown>;
      return { mode: parsed.mode as M, timeLeft: parsed.timeLeft, counter: extractCounter(o) ?? 0 };
    } catch {
      return { mode: defaultMode, timeLeft: defaultDuration, counter: 0 };
    }
  }

  function toPersistedData(state: TimerState<M>): Record<string, unknown> {
    return {
      mode: state.mode,
      timeLeft: state.timeLeft,
      counter: state.counter,
      [config.legacyCounterKey]: state.counter,
      updatedAt: Date.now(),
    };
  }

  function persistLocal(state: TimerState<M>): void {
    localStorage.setItem(storageKey, JSON.stringify(toPersistedData(state)));
  }

  async function persistServer(state: TimerState<M>): Promise<void> {
    if (!hasUserId()) return;
    await saveTimerState(serverKey, toPersistedData(state)).catch(() => undefined);
  }

  function persistAll(state: TimerState<M>): void {
    persistLocal(state);
    void persistServer(state);
  }

  const timerType = serverKey as "pomodoro" | "twenty";

  return function useTimer(): TimerHookReturn<M> {
    const [state, setState] = useState<TimerState<M>>(() => ({
      isRunning: false,
      ...loadLocal(),
    }));

    const intervalRef = useRef<number | null>(null);
    const stateRef = useRef(state);
    stateRef.current = state;

    useEffect(() => {
      if (!hasUserId()) return;
      fetchTimerState(serverKey)
        .then((raw) => {
          if (!isValidPersisted(raw, validModes)) return;
          const o = raw as unknown as Record<string, unknown>;
          const serverUpdatedAt = typeof o.updatedAt === "number" ? o.updatedAt : 0;
          // Ne remplacer que si le serveur est plus recent que le localStorage
          const localRaw = localStorage.getItem(storageKey);
          if (localRaw) {
            try {
              const localParsed = JSON.parse(localRaw) as Record<string, unknown>;
              const localUpdatedAt = typeof localParsed.updatedAt === "number" ? localParsed.updatedAt : 0;
              if (localUpdatedAt >= serverUpdatedAt) return;
            } catch { /* localStorage illisible, on laisse le serveur gagner */ }
          }
          const counter = extractCounter(o) ?? 0;
          setState((prev) => {
            if (prev.isRunning) return prev;
            return { isRunning: false, mode: raw.mode as M, timeLeft: raw.timeLeft, counter };
          });
          persistLocal({ isRunning: false, mode: raw.mode as M, timeLeft: raw.timeLeft, counter });
        })
        .catch(() => undefined);
    }, []);

    useEffect(() => {
      const handleUnload = () => persistLocal(stateRef.current);
      window.addEventListener("beforeunload", handleUnload);
      return () => window.removeEventListener("beforeunload", handleUnload);
    }, []);

    const transitionMode = useCallback((fromSkip = false) => {
      let endedMode: M = defaultMode;
      setState((prev) => {
        endedMode = prev.mode;
        const transition = config.getTransition(prev);
        const next: TimerState<M> = {
          isRunning: config.getAutoStart(fromSkip),
          mode: transition.mode,
          timeLeft: transition.timeLeft,
          counter: transition.counter,
        };
        persistLocal(next);
        void persistServer(next);
        return next;
      });
      config.onTransitionSound(endedMode);
      // Browser notification when timer ends (useful when tab is in background)
      if (config.getNotificationMessage) {
        const msg = config.getNotificationMessage(endedMode);
        if (msg && getNotificationSettings().timerNotifications) {
          void sendNotification(msg, `timer-${serverKey}`);
        }
      }
    }, []);

    useEffect(() => {
      if (!state.isRunning) {
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }

      intervalRef.current = window.setInterval(() => {
        const current = stateRef.current;
        if (current.timeLeft <= 1) {
          transitionMode();
          return;
        }
        setState((prev) => {
          const next = { ...prev, timeLeft: prev.timeLeft - 1 };
          persistLocal(next);
          return next;
        });
      }, 1000);

      return () => {
        if (intervalRef.current !== null) clearInterval(intervalRef.current);
      };
    }, [state.isRunning, transitionMode]);

    const start = useCallback(() => {
      setState((prev) => {
        const next = { ...prev, isRunning: true };
        persistAll(next);
        return next;
      });
      track("timer_started", { type: timerType });
    }, []);

    const pause = useCallback(() => {
      setState((prev) => {
        const next = { ...prev, isRunning: false };
        persistAll(next);
        return next;
      });
      track("timer_paused", { type: timerType });
    }, []);

    const reset = useCallback(() => {
      const next: TimerState<M> = { isRunning: false, timeLeft: config.getDefaultDuration(), mode: defaultMode, counter: 0 };
      persistAll(next);
      setState(next);
      track("timer_reset", { type: timerType });
    }, []);

    const skip = useCallback(() => {
      transitionMode(true);
      track("timer_skipped", { type: timerType });
    }, [transitionMode]);

    return { state, start, pause, reset, skip };
  };
}
